import { useCallback, useMemo } from "react";
import { formatCurrency } from "@/utils/ranges.util";
import { CompanyDetailsFinancialsEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyDetailsFinancialsEntryDto";
import { useTranslation } from "react-i18next";
import { HiMagnifyingGlass } from "react-icons/hi2";

import { useCompanyFinancials } from "@/hooks/api/company/useCompanyFinancials";
import LineChartGradient from "@/components/molecules/charts/LineChartGradient";
import FeaturedText from "@/components/molecules/FeaturedText";

type Props = {
  id: string;
};

const CompanyDetailFinancials = ({ id }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useCompanyFinancials({ id, enabled: !!id });

  const sortedData = useMemo(() => data?.entries.sort((a, b) => a.year - b.year), [data]);

  const getSortedDataByKey = useCallback(
    (key: keyof CompanyDetailsFinancialsEntryDto) =>
      sortedData?.map((item) => ({
        year: item.year,
        value: item[key],
      })),
    [sortedData],
  );

  const earningsChartData = getSortedDataByKey("earnings");
  const hasEarnings = useMemo(() => sortedData?.some((item) => item.earnings !== null), [sortedData]);

  const revenueChartData = getSortedDataByKey("revenue");
  const hasRevenue = useMemo(() => sortedData?.some((item) => item.revenue !== null), [sortedData]);

  if (isLoading || !data)
    return (
      <div className={"py-7"}>
        <div className={"mb-1.5 h-6 w-[30%] animate-pulse rounded-full bg-gray-300"} />

        <div className={"h-5 w-3/5 animate-pulse rounded-full bg-gray-300"} />

        {Array.from({ length: 2 }).map(() => (
          <div className={"mt-6 h-48 w-full animate-pulse rounded-md bg-gray-300"} />
        ))}
      </div>
    );

  if (data.entries.length === 0)
    return (
      <div className={"py-10"}>
        <FeaturedText
          heading={t("search.noResults.title")}
          body={t("company.detail.overview.insights.financials.noData")}
          size={"lg"}
          featuredIcon={{
            icon: HiMagnifyingGlass,
            color: "primary",
            size: "xl",
          }}
        />
      </div>
    );

  return (
    <>
      <div className={"py-7"}>
        <h3 className={"mb-1.5 font-semibold text-primary"}>
          {t("company.detail.overview.insights.financials.title")}
        </h3>
        <p className={"text-sm text-tertiary"}>{t("company.detail.overview.insights.financials.description")}</p>
      </div>

      {hasEarnings && (
        <>
          <div>
            <div className={"mb-6 flex w-full justify-between gap-3"}>
              <div className={"text-sm font-medium text-primary"}>
                {t("company.detail.overview.insights.financials.earnings")}
              </div>

              {data.yoy_earnings && (
                <div>
                  <div className={"text-right text-sm font-semibold text-secondary"}>
                    {(data.yoy_earnings * 100).toFixed(2)} %
                  </div>
                  <div className={"text-xs text-quaternary"}>
                    {t("company.detail.overview.insights.financials.yearGrowth")}
                  </div>
                </div>
              )}
            </div>
            <LineChartGradient chartData={earningsChartData!} dataKeyValue={"value"} dataKeyXAxis={"year"} />
          </div>

          <hr className={"my-8 h-px w-full border-0 bg-quaternary"} />
        </>
      )}

      {hasRevenue && (
        <>
          <div>
            <div className={"mb-6 flex w-full justify-between gap-3"}>
              <div className={"text-sm font-medium text-primary"}>
                {t("company.detail.overview.insights.financials.revenue")}
              </div>

              {data.yoy_revenue && (
                <div>
                  <div className={"text-right text-sm font-semibold text-secondary"}>
                    {(data.yoy_revenue * 100).toFixed(2)} %
                  </div>
                  <div className={"text-xs text-quaternary"}>
                    {t("company.detail.overview.insights.financials.yearGrowth")}
                  </div>
                </div>
              )}
            </div>
            <LineChartGradient chartData={revenueChartData!} dataKeyValue={"value"} dataKeyXAxis={"year"} />
          </div>

          <hr className={"my-8 h-px w-full border-0 bg-quaternary"} />
        </>
      )}

      <div>
        <div className={"mb-6 text-sm font-medium text-primary"}>
          {t("company.detail.overview.insights.financials.generalOverview")}
        </div>

        <div className={"w-full overflow-auto rounded-lg border border-secondary"}>
          <table className={"grid border-separate border-spacing-0"}>
            <thead>
              <tr className={"flex"}>
                <th
                  className={
                    "relative flex min-w-44 flex-1 select-none border-b border-r border-b-primary border-r-secondary bg-secondary px-4 py-2.5 text-start text-sm font-medium text-tertiary last:border-r-0"
                  }
                />

                {sortedData?.map((item, index) => (
                  <th
                    key={index}
                    className={
                      "relative flex flex-1 select-none border-b border-r border-b-primary border-r-secondary bg-secondary px-4 py-2.5 text-start text-sm font-medium text-tertiary last:border-r-0"
                    }
                  >
                    {item.year}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <FinancialTableRow
                data={sortedData!}
                accessorKey={"earnings"}
                title={t("company.detail.overview.insights.financials.earnings")}
              />
              <FinancialTableRow
                data={sortedData!}
                accessorKey={"revenue"}
                title={t("company.detail.overview.insights.financials.revenue")}
              />
              <FinancialTableRow
                data={sortedData!}
                accessorKey={"balance_total"}
                title={t("company.detail.overview.insights.financials.balanceTotal")}
              />
              <FinancialTableRow
                data={sortedData!}
                accessorKey={"equity_ratio"}
                title={t("company.detail.overview.insights.financials.equityRatio")}
                formatter={(val) => `${Math.round(val).toFixed(2)} %`}
              />
              <FinancialTableRow
                data={sortedData!}
                accessorKey={"equity"}
                title={t("company.detail.overview.insights.financials.equity")}
              />
              <FinancialTableRow
                data={sortedData!}
                accessorKey={"receivables"}
                title={t("company.detail.overview.insights.financials.receivables")}
              />
              <FinancialTableRow
                data={sortedData!}
                accessorKey={"liabilities"}
                title={t("company.detail.overview.insights.financials.liabilities")}
              />
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default CompanyDetailFinancials;

type FinancialTableRowProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  accessorKey: string;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any) => string;
};

const FinancialTableRow = ({
  data,
  accessorKey,
  title,
  formatter = (val) => formatCurrency(val),
}: FinancialTableRowProps) => {
  const { t } = useTranslation();
  return (
    <tr className={"flex flex-1"}>
      <td
        className={
          "w-full min-w-44 truncate border-b border-r border-secondary px-4 py-2.5 text-start text-sm font-medium text-tertiary"
        }
      >
        {title}
      </td>
      {data.map((item, index) => (
        <td key={index} className={"w-full border-b border-r border-secondary px-4 py-2.5 text-start text-sm"}>
          {item[accessorKey] ? (
            <span className={"font-medium text-tertiary"}>{formatter(item[accessorKey])}</span>
          ) : (
            <span className={"text-quaternary"}>{t("company.detail.overview.insights.financials.unknown")}</span>
          )}
        </td>
      ))}
    </tr>
  );
};
